// Turns the raw season payload into the rows the page draws.
//
// A "Sunday" here is a DATE, which may hold the 10:00 English service, the
// 12:30 Spanish one, or both. They are separate records under separate
// churches (2 and 3), and they do NOT share a running order — the 10:00 might
// have nine picks where the 12:30 has four. So each keeps its own column and
// its own list. Nothing is paired.
import {
  SeasonData, SeasonService, SeasonSelection, SeasonSpanishSelection, OrganWork, Musician,
} from '../../services/seasonViewService';
import { seasonKey, SeasonKey } from './tokens';

/**
 * One line of the music lane: a slot label, then the 10:00 English pick and
 * the 12:30 Spanish pick SIDE BY SIDE. Either side may be empty — the two
 * services genuinely don't sing the same number of things.
 */
export interface Placement {
  key: string;
  slot: string;
  recurring: boolean;

  en: string;
  enSelectionId: number | null;
  enPerformanceType: string | null;
  enComposer: string | null;
  enHasFile: boolean;
  enAtOrgan: boolean;
  enNotes: string | null;

  es: string;
}

export interface SundayRow {
  date: string;             // YYYY-MM-DD
  dateLine: string;         // "SEP 6"
  dateLong: string;         // "Sunday 6 September"
  day: string;              // "Proper 18"
  season: SeasonKey;
  isFeast: boolean;
  gap: boolean;             // no offertory music of any kind at the 10:00
  blank: boolean;           // nothing chosen at either service
  decided: number;
  english: SeasonService | null;
  spanish: SeasonService | null;
  placements: Placement[];
  organ: OrganWork[];
  people: Musician[];
  ideas: string[];
  readings: { k: string; v: string }[];
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_LONG = ['January','February','March','April','May','June','July',
                     'August','September','October','November','December'];
const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

/** Parse YYYY-MM-DD at noon so a timezone shift can never move the day. */
export function parseDate(d: string): Date {
  return new Date(`${d}T12:00:00`);
}

export function shortDate(d: string): string {
  const dt = parseDate(d);
  return `${MONTHS[dt.getMonth()]} ${dt.getDate()}`;
}

export function longDate(d: string): string {
  const dt = parseDate(d);
  return `${DAYS[dt.getDay()]} ${dt.getDate()} ${MONTHS_LONG[dt.getMonth()]}`;
}

/**
 * Rough liturgical running order, used ONLY to place a Spanish pick that has
 * no English counterpart. Matched picks keep the order Jack put them in.
 */
const ORDER: [RegExp, number][] = [
  [/prelude/i, 0],
  [/entrance|processi|gathering|opening/i, 10],
  [/kyrie|asperges/i, 20],
  [/gloria/i, 30],
  [/psalm/i, 40],
  [/gospel|alleluia|acclamaci/i, 50],
  [/sequence|hymn i\b/i, 55],
  [/sermon/i, 60],
  [/creed/i, 65],
  [/offertory anthem/i, 70],
  [/offert/i, 75],
  [/sanctus|santo/i, 80],
  [/lord'?s prayer|padre nuestro/i, 85],
  [/fraction|cordero/i, 90],
  [/communion|comuni/i, 100],
  [/taize|taiz\u00e9/i, 105],
  [/closing|recessi|despedida|sending/i, 110],
  [/postlude/i, 120],
];

function slotRank(slot: string | null | undefined): number {
  const s = (slot || '').trim();
  for (const [re, n] of ORDER) if (re.test(s)) return n;
  return 95; // unknown slots land late but before the closing hymn
}

const DOXOLOGY = /praise god,? from whom all blessings flow/i;

function norm(s: string | null | undefined): string {
  return (s || '').trim().toLowerCase();
}

/** Song ideas are stored as free-form JSON; pull a title out of whatever shape. */
function ideaText(x: any): string {
  if (typeof x === 'string') return x;
  if (x && typeof x === 'object') return x.title || x.text || x.name || x.anthem_title || '';
  return '';
}

const EMPTY_LINE = {
  en: '', enSelectionId: null, enPerformanceType: null, enComposer: null,
  enHasFile: false, enAtOrgan: false, enNotes: null,
  es: '',
};

/**
 * Line the two services up by slot. Slot names drift ("Communion" vs
 * "Communion "), so match on the trimmed lowercase name and consume each
 * Spanish pick once. Unmatched picks on either side keep their line with the
 * other column blank — the 10:00 having nine items to the 12:30's four is the
 * normal state, not an error.
 */
function pairPlacements(en: SeasonSelection[], es: SeasonSpanishSelection[]): Placement[] {
  const pool = new Map<string, SeasonSpanishSelection[]>();
  es.forEach(s => {
    const k = norm(s.placement);
    if (!pool.has(k)) pool.set(k, []);
    pool.get(k)!.push(s);
  });

  const out: Placement[] = en.map(sel => {
    const match = pool.get(norm(sel.placement))?.shift() || null;
    const esTitle = match ? match.song_title : '';
    return {
      ...EMPTY_LINE,
      key: `en-${sel.service_selection_id}`,
      slot: (sel.placement || '').trim() || '—',
      recurring: !!sel.is_template_music,
      en: sel.song_title,
      enSelectionId: sel.service_selection_id,
      enPerformanceType: sel.performance_type,
      enComposer: sel.composer_name,
      enHasFile: !!sel.has_file,
      enAtOrgan: !!sel.at_organ,
      enNotes: sel.director_notes,
      es: esTitle,
    };
  });

  // Spanish picks with no English counterpart get their own line, slotted into
  // the running order rather than dumped at the bottom.
  const leftovers: SeasonSpanishSelection[] = [];
  pool.forEach(list => list.forEach(s => leftovers.push(s)));
  leftovers
    .sort((a, b) => slotRank(a.placement) - slotRank(b.placement) || a.order_number - b.order_number)
    .forEach(s => {
      const line: Placement = {
        ...EMPTY_LINE,
        key: `es-${s.spanish_song_selection_id}`,
        slot: (s.placement || '').trim() || '—',
        recurring: false,
        es: s.song_title,
      };
      const r = slotRank(s.placement);
      const at = out.findIndex(p => slotRank(p.slot) > r);
      if (at === -1) out.push(line); else out.splice(at, 0, line);
    });

  return out;
}

/** No offertory music of any kind — the design breaks the spine for this. */
function hasOffertoryGap(en: SeasonSelection[]): boolean {
  return !en.some(s =>
    norm(s.placement).startsWith('offertory') &&
    !s.is_template_music &&
    !DOXOLOGY.test(s.song_title || '')
  );
}

export function buildRows(data: SeasonData): SundayRow[] {
  const byDate = new Map<string, SeasonService[]>();
  data.services.forEach(s => {
    if (!byDate.has(s.liturgy_date)) byDate.set(s.liturgy_date, []);
    byDate.get(s.liturgy_date)!.push(s);
  });

  const selByService = new Map<number, SeasonSelection[]>();
  data.selections.forEach(s => {
    if (!selByService.has(s.service_id)) selByService.set(s.service_id, []);
    selByService.get(s.service_id)!.push(s);
  });

  const espByService = new Map<number, SeasonSpanishSelection[]>();
  data.spanishSelections.forEach(s => {
    if (!espByService.has(s.service_id)) espByService.set(s.service_id, []);
    espByService.get(s.service_id)!.push(s);
  });

  return Array.from(byDate.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, services]) => {
      // The English service is the earlier one (10:00); Spanish is the 12:30.
      const sorted = [...services].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
      const english = sorted[0] || null;
      const spanish = sorted.length > 1 ? sorted[sorted.length - 1] : null;
      const ref = english || spanish!;

      const enSel = english ? (selByService.get(english.service_id) || []) : [];
      // Spanish picks normally hang off the 12:30 record, but tolerate them
      // sitting on the 10:00 if the data was entered that way.
      const esSel = [
        ...(spanish ? espByService.get(spanish.service_id) || [] : []),
        ...(english ? espByService.get(english.service_id) || [] : []),
      ];

      const ids = services.map(s => s.service_id);
      const organ = data.organ.filter(o => ids.includes(o.service_id));
      const people = data.musicians.filter(m => ids.includes(m.service_id));

      const placements = pairPlacements(enSel, esSel);
      const decided = placements.filter(p => !p.recurring && (p.en || p.es)).length;

      const ideas = (ref.song_ideas || []).map(ideaText).filter(Boolean);

      const readings = [
        ['Old Testament', ref.ot_reading],
        ['Psalm', ref.psalm_reading],
        ['Epistle', ref.nt_reading],
        ['Gospel', ref.gospel_reading],
      ].filter(([, v]) => !!v).map(([k, v]) => ({ k: k as string, v: v as string }));

      return {
        date,
        dateLine: shortDate(date).toUpperCase(),
        dateLong: longDate(date),
        day: ref.liturgy_name || 'Service',
        season: seasonKey(ref.season, ref.is_special, ref.liturgy_name),
        isFeast: !!ref.is_special,
        gap: hasOffertoryGap(enSel),
        blank: placements.length === 0,
        decided,
        english,
        spanish,
        placements,
        organ,
        people,
        ideas,
        readings,
      };
    });
}
